/**
 * <sm-signup-popup> — opens the sign-up dialog, checks the email before it
 * goes and remembers the answer.
 *
 * Opens after a delay, a scroll depth or (desktop) the mouse leaving through
 * the top of the window, whichever comes first. Closing it holds it back for
 * data-days; signing up holds it back for good. Test mode skips both.
 *
 * The form is Shopify's customer form, sent in the background. Shopify
 * answers a good sign-up with a redirect and a bad one with a 400, so the
 * request stops at the redirect (redirect: 'manual') and the success panel
 * swaps in about a fifth of a second after the tap, with nothing downloaded.
 *
 * This relies on the store's "Enable hCaptcha on contact and comment forms"
 * being off (it was turned off on 2026-11-04). data-nocaptcha keeps
 * Shopify's form script from also posting the form itself. If Shopify
 * answers with anything but the redirect (the setting switched back on,
 * its "Verifying your connection" screen), the form is handed back to
 * Shopify's own full-page post, and the page returns on the success panel.
 */
class SmSignupPopup extends HTMLElement {
  static KEY_DONE = 'sm-signup:done';
  static KEY_CLOSED = 'sm-signup:closed';
  static KEY_TEASER_OFF = 'sm-signup:teaser-off';

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    if (!this.dialog || !this.form) return;

    this.designMode = this.hasAttribute('data-design-mode');
    this.test = this.dataset.test === 'true';

    this.querySelectorAll('[data-popup-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });
    // A click on the backdrop lands on the dialog itself, outside the panel.
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.form.addEventListener('submit', (e) => this.submit(e));
    this.form.dataset.nocaptcha = 'true';
    this.returnHere();

    this.teaser = this.querySelector('[data-popup-teaser]');
    this.teaser?.querySelector('[data-teaser-open]').addEventListener('click', () => {
      this.hideTeaser();
      this.open();
    });
    this.teaser?.querySelector('[data-teaser-dismiss]').addEventListener('click', () => {
      this.hideTeaser();
      if (!this.designMode) this.remember(SmSignupPopup.KEY_TEASER_OFF, String(Date.now()));
    });

    // Back from a normal post (no JavaScript at submit time, or a captcha).
    // Coming back from the post (or a page restored from the back button).
    window.addEventListener('pageshow', () => {
      this.posting = false;
      this.form.classList.remove('is-loading');
    });
    if (this.querySelector('[data-popup-step="success"][data-posted]')) {
      this.remember(SmSignupPopup.KEY_DONE);
      this.applyCode();
      // Tidy the address bar so a refresh or a shared link does not carry
      // the post marker or the form anchor.
      const url = new URL(window.location.href);
      url.searchParams.delete('customer_posted');
      url.hash = '';
      history.replaceState(history.state, '', url.pathname + url.search);
      this.open();
      return;
    }
    if (this.form.querySelector('[data-popup-error]:not([hidden])')) {
      this.open();
      return;
    }

    if (this.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.detail.sectionId === this.dataset.section) this.open();
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.detail.sectionId === this.dataset.section) this.close(false);
      });
      return;
    }

    if (!this.test && this.recall(SmSignupPopup.KEY_DONE)) return;

    // Anyone who closed it without signing up gets the reopen tab, on every
    // page and every visit, until they sign up or close the tab.
    if (this.recall(SmSignupPopup.KEY_CLOSED) && this.teaserWanted()) this.showTeaser();

    // Only shopping pages may open it by itself.
    if (this.dataset.auto !== 'true') return;
    if (!this.test && !this.eligible()) return;
    this.arm();
  }

  /**
   * Points the shop button back at this page, query string included. With the
   * code to apply it goes through /discount/CODE so the cart picks it up on
   * the way back; without one it simply closes the pop-up.
   */
  returnHere() {
    const wrap = this.querySelector('[data-popup-return]');
    const link = wrap?.querySelector('a');
    if (!link) return;
    if (wrap.dataset.popupReturn === 'close') {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.close(false);
      });
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.delete('customer_posted');
    const query = params.toString();
    const here = window.location.pathname + (query ? `?${query}` : '');
    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('redirect', here);
    link.href = url.pathname + url.search;
  }

  /**
   * Keeps every headline on the lines it was written on. The lines never
   * wrap (white-space: nowrap in the CSS); when the longest one is wider than
   * the column, the type comes down until it fits.
   */
  fitTitles() {
    this.querySelectorAll('.sm-popup__title').forEach((title) => {
      title.style.fontSize = '';
      const width = title.clientWidth;
      if (!width) return;
      const size = parseFloat(getComputedStyle(title).fontSize);
      if (title.scrollWidth > width) {
        title.style.fontSize = `${Math.floor(size * (width / title.scrollWidth) * 10) / 10}px`;
      }
    });
  }

  eligible() {
    if (this.recall(SmSignupPopup.KEY_DONE)) return false;
    const closed = Number(this.recall(SmSignupPopup.KEY_CLOSED) || 0);
    const days = Number(this.dataset.days || 7);
    return !closed || Date.now() - closed > days * 864e5;
  }

  arm() {
    const fire = () => {
      this.disarm();
      this.open();
    };
    this.disarm = () => {
      clearTimeout(this.timer);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('mouseout', this.onExit);
    };

    const delay = Number(this.dataset.delay || 0);
    this.timer = setTimeout(fire, delay * 1000);

    const depth = Number(this.dataset.scroll || 0);
    if (depth > 0) {
      this.onScroll = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 0 && (window.scrollY / max) * 100 >= depth) fire();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    if (this.dataset.exit === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.onExit = (e) => {
        if (!e.relatedTarget && e.clientY <= 0) fire();
      };
      document.addEventListener('mouseout', this.onExit);
    }
  }

  open() {
    if (this.dialog.open) return;
    // Never stack on top of another open drawer or modal, and never cut in
    // while someone is mid-search or typing in a field: try again shortly.
    if (!this.designMode && this.busy()) {
      setTimeout(() => this.open(), 4000);
      return;
    }
    this.disarm?.();
    this.hideTeaser();
    this.dialog.showModal();
    this.dialog.classList.add('is-open');
    this.fitTitles();
    if (!this.fitBound) {
      this.fitBound = true;
      window.addEventListener('resize', () => this.dialog.open && this.fitTitles());
      document.fonts?.ready.then(() => this.dialog.open && this.fitTitles());
    }
    document.documentElement.classList.add('sm-popup-open');
    const success = this.querySelector('[data-popup-step="success"]:not([hidden])');
    if (!success && !window.matchMedia('(pointer: coarse)').matches) {
      this.form.querySelector('input[type="email"]')?.focus({ preventScroll: true });
    }
  }

  busy() {
    if (document.querySelector('dialog[open]:not(.sm-popup), details[open].menu-drawer-container, cart-drawer.active, details-modal details[open], predictive-search[open]')) return true;
    const field = document.activeElement;
    return !!field && field.matches('input, textarea, select, [contenteditable="true"]') && !this.contains(field);
  }

  close(remember = true) {
    if (!this.dialog.open) return;
    if (remember && !this.designMode && !this.recall(SmSignupPopup.KEY_DONE)) {
      this.remember(SmSignupPopup.KEY_CLOSED, String(Date.now()));
    }
    this.dialog.classList.remove('is-open');
    document.documentElement.classList.remove('sm-popup-open');
    if (remember && !this.recall(SmSignupPopup.KEY_DONE) && (this.designMode || this.teaserWanted())) {
      setTimeout(() => this.showTeaser(), 320);
    }
    const done = () => this.dialog.close();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, 280);
  }

  /** Once someone closes the tab itself, it stays gone. */
  teaserWanted() {
    if (!this.teaser) return false;
    return this.test || !this.recall(SmSignupPopup.KEY_TEASER_OFF);
  }

  showTeaser() {
    if (!this.teaser || this.dialog.open) return;
    this.teaser.hidden = false;
    requestAnimationFrame(() => this.teaser.classList.add('is-visible'));
  }

  hideTeaser() {
    if (!this.teaser) return;
    this.teaser.classList.remove('is-visible');
    this.teaser.hidden = true;
  }

  submit(e) {
    // Handed back to Shopify (captcha switched on again): let it through.
    if (this.handedOff) return;
    if (this.posting) {
      e.preventDefault();
      return;
    }
    const input = this.form.querySelector('input[type="email"]');
    const error = this.form.querySelector('[data-popup-error]');
    // Stricter than the browser's own check (which accepts "name@gmail"),
    // because Shopify refuses those and the pop-up should say so first.
    const email = input.value.trim();
    if (!email || !input.checkValidity() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      e.preventDefault();
      this.showError(error.dataset.invalid, input, error);
      return;
    }
    // In the editor nothing is sent; the success panel is shown so it can be styled.
    if (this.designMode) {
      e.preventDefault();
      this.success();
      return;
    }
    if (!window.fetch) return;
    e.preventDefault();
    this.send(input, error);
  }

  async send(input, error) {
    this.posting = true;
    this.form.classList.add('is-loading');
    error.hidden = true;
    try {
      const res = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        redirect: 'manual',
        credentials: 'same-origin',
      });
      if (res.type === 'opaqueredirect' || res.redirected) {
        this.success();
        return;
      }
      // Anything else (captcha switched back on, Shopify's "Verifying your
      // connection" screen, a refusal) goes to Shopify's own full-page post,
      // so the shopper sees Shopify's answer rather than a wrong guess.
      this.handOff();
    } catch (err) {
      this.handOff();
    } finally {
      this.posting = false;
      this.form.classList.remove('is-loading');
    }
  }

  /** Lets Shopify's own form handling (and its captcha) take the post. */
  handOff() {
    this.handedOff = true;
    delete this.form.dataset.nocaptcha;
    if (this.form.requestSubmit) this.form.requestSubmit();
    else HTMLFormElement.prototype.submit.call(this.form);
  }

  /**
   * Applies the code to the shopper's session in the background, the same way
   * visiting /discount/CODE does, so it is already on the cart at checkout.
   * Once it has taken, the shop button just closes the pop-up and leaves
   * them where they were; if it failed, the button still goes through
   * /discount/CODE as a fallback.
   */
  async applyCode() {
    const code = this.dataset.code;
    if (!code || !window.fetch) return;
    try {
      const root = window.Shopify?.routes?.root || '/';
      // Stops at Shopify's redirect: the discount cookie is set on that
      // response, so there is no page to download.
      const res = await fetch(`${root}discount/${encodeURIComponent(code)}`, {
        credentials: 'same-origin',
        redirect: 'manual',
      });
      if (res.type !== 'opaqueredirect' && !res.ok) return;
      this.applied = true;
      const link = this.querySelector('[data-popup-return="apply"] a');
      link?.addEventListener('click', (e) => {
        e.preventDefault();
        this.close(false);
      });
      document.dispatchEvent(new CustomEvent('sm:discount-applied', { detail: { code } }));
    } catch (e) {}
  }

  showError(text, input, error) {
    error.textContent = text;
    error.hidden = false;
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', error.id);
    input.focus();
  }

  success() {
    this.hideTeaser();
    if (!this.designMode) {
      this.remember(SmSignupPopup.KEY_DONE);
      this.applyCode();
    }
    this.querySelector('[data-popup-step="signup"]').hidden = true;
    const step = this.querySelector('[data-popup-step="success"]');
    step.hidden = false;
    this.fitTitles();
    step.setAttribute('tabindex', '-1');
    step.focus({ preventScroll: true });
  }

  remember(key, value = '1') {
    try {
      localStorage.setItem(key, value);
    } catch (e) {}
  }

  recall(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
}

if (!customElements.get('sm-signup-popup')) customElements.define('sm-signup-popup', SmSignupPopup);
